import { MenuItem, Select, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { formatPickupTime, isPickupTimeError } from "shared-ui/MenuOrder/utils";
import { PickupTime } from "shared-ui/MenuOrder/usePickupTimeState";
import { useClientTimezone } from "shared-ui/contexts/ClientTimezone";
import { useEffect, useState } from "react";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: 'column',
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  })
);

interface MenuOrderFormTimePickerProps {
  className?: string;
  forOrder: boolean;
  pickupTime: PickupTime | null;
  pickupTimes: PickupTime[];
  onChangePickupTime: (pickupTime: PickupTime) => void;
  mealMenuTimezone: string;
  disabled: boolean;
  openTimePicker?: boolean;
}

const KioskMenuOrderFormTimePicker = ({
  className,
  forOrder,
  disabled,
  onChangePickupTime,
  pickupTime,
  pickupTimes,
  mealMenuTimezone,
  openTimePicker,
}: MenuOrderFormTimePickerProps) => {
  const classes = useStyles();
  const clientTimezone = useClientTimezone();

  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (openTimePicker !== undefined) {
      setPickerOpen(true);
    }
  }, [openTimePicker]);

  const pickupTimesWithFormat = pickupTimes.map(pickupTime => ({
    pickupTime,
    format: formatPickupTime({
      pickupTime,
      mealMenuTimezone,
      clientTimezone,
      forLog: !forOrder,
    }),
  }));

  const matchingPickupTime = pickupTimes.find(pt => {
    if (pickupTime?.type === "Time") {
      return pt.type === "Time" && pt.value === pickupTime.value;
    } else {
      return pt.type === "ASAP";
    }
  });

  const error = isPickupTimeError(pickupTime, forOrder);

  return (
    <div className={classes.container}>
      <Typography variant="subtitle1Semibold" color={"mediumEmphasisTextLight"} sx={{ mt: 1 }}>Pickup Time</Typography>
      <Select
        onOpen={() => setPickerOpen(true)}
        onClose={() => setPickerOpen(false)}
        open={pickerOpen}
        className={className}
        error={error}
        disabled={disabled}
        value={
          pickupTime &&
          formatPickupTime({
            pickupTime,
            mealMenuTimezone,
            clientTimezone,
            forLog: !forOrder,
          })
        }
        onChange={e => {
          const matchingPickupTime = pickupTimesWithFormat.find(pt => pt.format === e.target.value);
          if (matchingPickupTime) {
            onChangePickupTime(matchingPickupTime.pickupTime);
          }
        }}
      >
        {pickupTimesWithFormat.map(({ format }) => (
          <MenuItem key={format} value={format}>
            {format}
          </MenuItem>
        ))}
        {pickupTime && !matchingPickupTime && (
          <MenuItem
            value={formatPickupTime({
              pickupTime,
              mealMenuTimezone,
              clientTimezone,
              forLog: !forOrder,
            })}
          >
            {formatPickupTime({
              pickupTime,
              mealMenuTimezone,
              clientTimezone,
              forLog: !forOrder,
            })}
          </MenuItem>
        )}
      </Select>
    </div>
  );
};

export default KioskMenuOrderFormTimePicker;
